import { useCallback, useEffect, useState } from 'react';

import { redirect } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useNavigate, useSearchParams, useLoaderData } from 'react-router';

import { useAuth } from '@clerk/react-router';
import { getAuth } from '@clerk/react-router/ssr.server';

import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Briefcase,
  TrendingUp,
  Search,
  MessageCircle,
  Users,
  Circle,
  Target,
} from 'lucide-react';

import { PLANS } from '~/config/plans';
import Header from '~/components/Header';
import PromoBanner from '~/components/PromoBanner';
import Hero from '~/components/Hero';
import Footer from '~/components/Footer';
import type { Feature } from '~/components/UseCaseFeatures';
import type { Step } from '~/components/HowItWorks';
import { createLogger } from '~/utils/logger.server';
import { getPostAuthPath } from '~/utils/auth.server';
import TryForFreeButton from '~/components/TryForFreeButton';
import Reviews from '~/components/Reviews';
import PrivacySection from '~/components/PrivacySection';
import PricingSection from '~/components/PricingSection';
import FAQSection from '~/components/FAQSection';
import LandedCompanies from '~/components/LandedCompanies';

export const handle = {
  title: 'Pavone - AI Job Interview Practice',
  description: 'Practice job interview answers with AI-powered feedback. Get real-time analysis on your speech, tone, and delivery. Try for free.',
};

const serverLogger = createLogger('HomeRoute');

export const meta: MetaFunction = () => {
  return [
    { title: 'Pavone - AI Job Interview Practice' },
    {
      name: 'description',
      content: 'Practice job interview answers with AI-powered feedback. Get real-time analysis on your speech, tone, and delivery. Try for free.',
    },
    { name: 'keywords', content: 'AI speaking coach, job interview practice, interview preparation, mock interview, interview coaching, AI feedback, presentation skills, public speaking practice' },
    { property: 'og:title', content: 'Pavone - AI Job Interview Practice' },
    { property: 'og:description', content: 'Practice job interview answers with AI-powered feedback. Get real-time analysis on your speech, tone, and delivery.' },
    { property: 'og:url', content: 'https://pavone.ai' },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: 'https://pavone.ai/images/demo.png' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: 'Pavone - AI Job Interview Practice' },
    { name: 'twitter:description', content: 'Practice job interview answers with AI-powered feedback. Try for free.' },
    { name: 'twitter:image', content: 'https://pavone.ai/images/demo.png' },
  ];
};

export async function loader(args: LoaderFunctionArgs) {
  try {
    const { userId } = await getAuth(args);
    const url = new URL(args.request.url);
    const referer = args.request.headers.get('referer') || '';

    const cameFromAuth =
      referer.includes('/sign-in') ||
      referer.includes('/sign-up') ||
      referer.includes('/sign-in/sso-callback') ||
      referer.includes('/auth.callback');

    if (userId && url.searchParams.get('signOut') !== 'true' && cameFromAuth) {
      const postAuthPath = getPostAuthPath();
      await serverLogger.info('Redirecting authenticated user from /use-cases/interview-preparation to configured post-auth path after auth', {
        metadata: { path: postAuthPath },
      });
      return redirect(postAuthPath);
    }

    await serverLogger.info('Interview use case route loaded', {
      userId: userId || undefined,
      metadata: { event: 'route_load' },
    });
    return Response.json({ freePractices: PLANS.free.practiceLimit });
  } catch (error) {
    await serverLogger.error('Failed to load interview use case route', {
      metadata: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    throw error;
  }
}

// 3-step looping demo component
// Typing animation component
const TypingText = ({ text, delay = 0, className = "" }: { text: string; delay?: number; className?: string }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    setIsTyping(false);
    setDisplayedText("");
    
    let typingInterval: NodeJS.Timeout | null = null;
    
    const startTyping = setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;
      
      typingInterval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          if (typingInterval) {
            clearInterval(typingInterval);
            typingInterval = null;
          }
          setIsTyping(false);
        }
      }, 50); // 50ms per character for realistic typing speed
    }, delay * 1000);

    return () => {
      clearTimeout(startTyping);
      if (typingInterval) {
        clearInterval(typingInterval);
      }
    };
  }, [text, delay]);

  return (
    <span className={className}>
      {displayedText}
      {isTyping && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.8, repeat: Infinity }}
          className="inline-block w-0.5 h-4 bg-current ml-0.5 align-middle"
        />
      )}
    </span>
  );
};

const stepVariants = {
  enter: { opacity: 0, y: 20, scale: 0.97 },
  center: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  exit: { opacity: 0, y: -15, scale: 0.97, transition: { duration: 0.25, ease: 'easeIn' } },
};

// === InterviewDemo_v3 ===
// Improvements: AnimatePresence, no GPU-heavy blobs, 19s cycle, step indicators,
// better demo content, client-only animation (prevents hydration mismatch)

const InterviewDemo_v3 = () => {
  const [mounted, setMounted] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!mounted) return;
    const durations = [6000, 6500, 6500];
    let timeout: NodeJS.Timeout;

    const advance = () => {
      setStep(prev => {
        const next = (prev + 1) % 3;
        timeout = setTimeout(advance, durations[next]);
        return next;
      });
    };

    timeout = setTimeout(advance, durations[0]);
    return () => clearTimeout(timeout);
  }, [mounted]);

  return (
    <div className="relative rounded-2xl overflow-hidden">
      <div className="relative w-full min-h-[280px] sm:min-h-[380px] md:min-h-[480px] p-3 sm:p-5 md:p-6 flex items-center justify-center overflow-hidden">

        {!mounted ? (
          /* Static SSR placeholder — no motion elements, matches server render exactly */
          <div className="w-full flex items-center justify-center">
            <div className="w-full max-w-[95%] sm:max-w-md bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-dark-blue/10 flex items-center justify-center">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-brand-dark-blue" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-brand-dark-blue"><Trans>Personalize your interview</Trans></h3>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Role</Trans></label>
                  <div className="border-2 border-brand-dark-blue/20 rounded-lg px-3 py-2 sm:px-4 sm:py-3 bg-brand-dark-blue/5 min-h-[2rem]" />
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Company</Trans></label>
                  <div className="border-2 border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 bg-gray-50 min-h-[2rem]" />
                </div>
                <div>
                  <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Context</Trans></label>
                  <div className="border-2 border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 bg-gray-50 min-h-[2rem]" />
                </div>
                <div className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-dark-blue/80 text-white rounded-lg py-2 sm:py-3 text-sm sm:text-base font-semibold flex items-center justify-center gap-2 opacity-0">
                  <span><Trans>Start interview</Trans></span>
                  <span>→</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <AnimatePresence mode="wait">
          {/* Step 1: Personalize Interview */}
          {step === 0 && (
            <motion.div
              key="step-0"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full flex items-center justify-center"
            >
              <div className="w-full max-w-[95%] sm:max-w-md bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-dark-blue/10 flex items-center justify-center">
                    <Search className="w-4 h-4 sm:w-5 sm:h-5 text-brand-dark-blue" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-brand-dark-blue"><Trans>Personalize your interview</Trans></h3>
                </div>

                <div className="space-y-2 sm:space-y-3">
                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Role</Trans></label>
                    <div className="border-2 border-brand-dark-blue/20 rounded-lg px-3 py-2 sm:px-4 sm:py-3 bg-brand-dark-blue/5">
                      <TypingText text="Senior Product Manager" delay={0.3} className="text-brand-dark-blue font-medium text-sm sm:text-base" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Company</Trans></label>
                    <div className="border-2 border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 bg-gray-50">
                      <TypingText text="Meta Inc" delay={1.8} className="text-gray-700 text-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs sm:text-sm font-medium text-gray-700 block mb-1"><Trans>Context</Trans></label>
                    <div className="border-2 border-gray-200 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 bg-gray-50 min-h-[2rem] sm:min-h-[2.5rem]">
                      <TypingText text="Focus on AI products, 5 years PM experience" delay={2.7} className="text-gray-700 text-sm" />
                    </div>
                  </div>

                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 4 }}
                    className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-dark-blue/80 text-white rounded-lg py-2 sm:py-3 text-sm sm:text-base font-semibold hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
                  >
                    <span><Trans>Start interview</Trans></span>
                    <motion.div animate={{ x: [0, 3, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 4.5 }}>
                      →
                    </motion.div>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Q&A Chat */}
          {step === 1 && (
            <motion.div
              key="step-1"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full flex items-center justify-center"
            >
              <div className="w-full max-w-[95%] sm:max-w-xl md:max-w-2xl space-y-3 sm:space-y-4">
                {/* Question from interviewer */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex items-start gap-2 sm:gap-3 bg-white rounded-xl p-3 sm:p-4 shadow-md border-2 border-brand-dark-blue/20"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-dark-blue flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-brand-dark-blue text-sm sm:text-base md:text-lg">
                      <Trans>Tell me about yourself.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* User answer — decent but improvable */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="flex items-start gap-2 sm:gap-3 justify-end"
                >
                  <div className="bg-gray-100 rounded-xl p-3 sm:p-4 shadow-md max-w-[85%] sm:max-w-lg border-2 border-gray-200">
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1.5, delay: 1.6 }}
                      className="text-gray-800 text-xs sm:text-sm md:text-base"
                    >
                      I've been in product management for 5 years, mostly in B2B SaaS. I led the launch of our analytics dashboard which grew to 50K users...
                    </motion.p>
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-gold flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                </motion.div>

                {/* Follow-up question — real text */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 4 }}
                  className="flex items-start gap-2 sm:gap-3 bg-white rounded-xl p-3 sm:p-4 shadow-md border-2 border-brand-dark-blue/20"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-dark-blue flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-brand-dark-blue text-xs sm:text-sm md:text-base">
                      <Trans>What specific metrics did the dashboard improve for your clients?</Trans>
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Interview Readiness + Focus Areas */}
          {step === 2 && (
            <motion.div
              key="step-2"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full flex items-center justify-center"
            >
              <div className="w-full max-w-[95%] sm:max-w-lg md:max-w-xl space-y-3 sm:space-y-4">
                {/* Interview Readiness card */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="bg-white rounded-xl p-3 sm:p-5 shadow-lg border-2 border-brand-gold/20"
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-gold/10 flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-brand-gold" />
                    </div>
                    <h3 className="text-xs sm:text-sm font-bold text-brand-dark-blue">
                      <Trans>Interview Readiness</Trans>
                    </h3>
                  </div>
                  <div className="flex gap-4 sm:gap-6">
                    {/* Circular gauge */}
                    <div className="flex flex-col items-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.6, type: 'spring' }}
                        className="relative w-16 h-16 sm:w-20 sm:h-20"
                      >
                        <svg className="w-16 h-16 sm:w-20 sm:h-20 -rotate-90" viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray="72 100" strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-base sm:text-lg font-bold text-brand-dark-blue">72%</span>
                        </div>
                      </motion.div>
                      <span className="text-[10px] sm:text-xs font-medium text-brand-dark-blue/60 mt-1">
                        <Trans>Good</Trans>
                      </span>
                    </div>
                    {/* Communication Level bar */}
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] sm:text-xs font-medium text-brand-dark-blue/60">
                          <Trans>Communication Level</Trans>
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-brand-dark-blue">6.8/10</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: '0%' }}
                          animate={{ width: '68%' }}
                          transition={{ duration: 0.8, delay: 0.8 }}
                          className="h-full bg-gradient-to-r from-brand-gold to-brand-dark-blue rounded-full"
                        />
                      </div>
                      <span className="text-[10px] sm:text-xs text-brand-dark-blue/50 mt-1">
                        <Trans>Mid-Level</Trans>
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* What to Focus on Next card */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.5 }}
                  className="bg-white rounded-xl p-3 sm:p-5 shadow-lg border-2 border-brand-success/20"
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-brand-success/10 flex items-center justify-center">
                      <Target className="w-4 h-4 sm:w-5 sm:h-5 text-brand-success" />
                    </div>
                    <h3 className="text-xs sm:text-sm font-bold text-brand-dark-blue">
                      <Trans>What to Focus on Next</Trans>
                    </h3>
                  </div>
                  <ul className="space-y-2 sm:space-y-2.5">
                    <motion.li initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="flex items-start gap-2 sm:gap-2.5">
                      <Circle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 text-red-500" fill="currentColor" />
                      <span className="text-xs sm:text-sm text-brand-dark-blue/80 leading-relaxed"><Trans>Reduce filler words in your opening sentence</Trans></span>
                    </motion.li>
                    <motion.li initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5 }} className="flex items-start gap-2 sm:gap-2.5">
                      <Circle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 text-brand-gold" fill="currentColor" />
                      <span className="text-xs sm:text-sm text-brand-dark-blue/80 leading-relaxed"><Trans>Structure your answer with a clear beginning, middle, and end</Trans></span>
                    </motion.li>
                    <motion.li initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }} className="flex items-start gap-2 sm:gap-2.5">
                      <Circle className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 text-brand-success" fill="currentColor" />
                      <span className="text-xs sm:text-sm text-brand-dark-blue/80 leading-relaxed"><Trans>Show more energy and confidence in your tone</Trans></span>
                    </motion.li>
                  </ul>
                  <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-brand-dark-blue/50">
                      <div className="flex items-center gap-1">
                        <Circle className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-red-500" fill="currentColor" />
                        <span><Trans>High priority</Trans></span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Circle className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-brand-gold" fill="currentColor" />
                        <span><Trans>Medium</Trans></span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Circle className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-brand-success" fill="currentColor" />
                        <span><Trans>Low</Trans></span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        )}
      </div>

      {/* Step indicators */}
      {mounted && (
        <div className="flex items-center justify-center gap-2 mt-3 pb-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                step === i ? 'w-6 bg-brand-dark-blue' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Interview = () => {
  const { t } = useLingui();
  const { freePractices } = useLoaderData<typeof loader>();
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [promoBarVisible, setPromoBarVisible] = useState(true);
  const [promoBarHeight, setPromoBarHeight] = useState(0);
  const handlePromoVisibility = useCallback((visible: boolean) => {
    setPromoBarVisible(visible);
  }, []);
  const handlePromoHeight = useCallback((height: number) => {
    setPromoBarHeight(height);
  }, []);
  
  // Get post-auth path from environment variable (defaults to /practice)
  const postAuthPath = typeof window !== 'undefined' 
    ? (window.ENV?.POST_AUTH_PATH || '/practice')
    : '/practice';

  const features: Feature[] = [
    {
      icon: Briefcase,
      iconColor: 'text-brand-dark-blue',
      iconBgColor: 'bg-brand-dark-blue/10',
      title: 'Practice Anywhere, Anytime',
      description: 'No scheduling mock interviews with friends. Practice on your schedule, as many times as you need. Available 24/7.',
      badge: 'ALWAYS AVAILABLE',
      badgeColor: 'bg-brand-gold/20 text-brand-gold'
    },
    {
      icon: Brain,
      iconColor: 'text-brand-gold',
      iconBgColor: 'bg-brand-gold/10',
      title: 'Unbiased AI Feedback',
      description: 'Get honest, objective analysis of your answers. No judgement, just actionable insights to improve your interview performance.',
      badge: 'AI POWERED',
      badgeColor: 'bg-brand-gold/20 text-brand-gold'
    },
    {
      icon: TrendingUp,
      iconColor: 'text-brand-green',
      iconBgColor: 'bg-brand-green/10',
      title: 'Track Your Progress',
      description: 'Watch your confidence grow with every practice session. See improvement metrics and know exactly when you\'re interview-ready.',
      badge: 'TRACK GROWTH',
      badgeColor: 'bg-brand-gold/20 text-brand-gold'
    }
  ];

  const steps: Step[] = [
    {
      number: 1,
      title: 'Practice Common Questions',
      description: 'Choose from "Tell me about yourself," behavioral questions, or practice your own custom questions. Record your answers just like a real interview.',
      badge: 'PRACTICE',
      badgeColor: 'bg-gray-400'
    },
    {
      number: 2,
      title: 'Get Instant AI Feedback',
      description: 'Our AI analyzes your answer structure, communication clarity, confidence level, eye contact, and delivery. See exactly what hiring managers see.',
      badge: 'AI ANALYSIS',
      badgeColor: 'bg-gradient-to-r from-brand-gold to-brand-gold/80'
    },
    {
      number: 3,
      title: 'Improve & Get Hired',
      description: 'Practice until your answers are polished and confident. Track your progress over time. Walk into your real interview prepared and get that offer.',
      badge: 'GET HIRED',
      badgeColor: 'bg-gray-400'
    }
  ];

  // Initialize custom Meta Pixel for interview landing page
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Check if fbq already exists, if not initialize it
      if (!window.fbq) {
        (function(f: any, b: any, e: string, v: string) {
          var n: any, t: any, s: any;
          if (f.fbq) return;
          n = f.fbq = function() {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = true;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      }
      
      // Initialize the interview-specific pixel
      if (window.fbq) {
        window.fbq('init', '1490311458865860');
        window.fbq('track', 'PageView');
      }
    }
  }, []);

  // Track interview page view with additional context
  useEffect(() => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: 'Job Interview Use Case Landing Page',
        content_category: 'Interview',
      });
    }
  }, []);

  useEffect(() => {
    if (isLoaded && isSignedIn && searchParams.get('signOut') === 'true') {
      signOut();
    }
  }, [isLoaded, isSignedIn, searchParams, signOut]);

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg"><Trans>Loading...</Trans></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Meta Pixel Noscript */}
      <noscript>
        <img 
          height="1" 
          width="1" 
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=1490311458865860&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
      
      <PromoBanner onVisibilityChange={handlePromoVisibility} onHeightChange={handlePromoHeight} />
      <Header topOffset={promoBarVisible ? promoBarHeight : 0} />

      <div id="mobile-backdrop" className="hidden"></div>
      
      {/* Vertical grid lines */}
      <div className="fixed inset-0 pointer-events-none z-10">
        <div className="absolute left-1/2 top-0 h-full w-px bg-grid-line" style={{ transform: 'translateX(-540px)' }}></div>
        <div className="absolute left-1/2 top-0 h-full w-px border-l border-dashed border-grid-line" style={{ transform: 'translateX(-270px)' }}></div>
        <div className="absolute left-1/2 top-0 h-full w-px border-l border-dashed border-grid-line" style={{ transform: 'translateX(0px)' }}></div>
        <div className="absolute left-1/2 top-0 h-full w-px border-l border-dashed border-grid-line" style={{ transform: 'translateX(270px)' }}></div>
        <div className="absolute left-1/2 top-0 h-full w-px bg-grid-line" style={{ transform: 'translateX(540px)' }}></div>
      </div>
      
      <main className="flex-1 relative z-20" style={{ paddingTop: promoBarVisible ? promoBarHeight : 0, transition: 'padding-top 0.3s ease' }}>
        <Hero 
          title={
            <>
              <span className="inline-flex items-center gap-1.5 mb-5 px-3 py-1 rounded-full bg-white/80 text-brand-dark-blue text-xs font-semibold tracking-wide border border-gray-200 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold"></span>
                <Trans>Sound confident and senior in interviews</Trans>
              </span>
              <span className="text-brand-dark-blue block leading-[1.1]">
                <Trans>Your</Trans>{' '}
                <span className="text-brand-gold"><Trans>AI</Trans></span>
                <br />
                <Trans>Interview</Trans>
                <br />
                <Trans>Practice</Trans>
              </span>
            </>
          }
          subtitle={
            <span className="text-gray-500">
              <Trans>Practice real questions. Get real insight.</Trans>
            </span>
          }
          demo={<InterviewDemo_v3 />}
        />

        <LandedCompanies />

        {/* How It Works */}
        {/* <HowItWorks 
          title="How It Works"
          subtitle="Practice interview answers like you're in a real interview — get AI feedback instantly"
          steps={steps}
        /> */}

        {/* Features */}
        {/* <UseCaseFeatures
          title="Why Job Seekers Choose Pavone"
          subtitle="Everything you need to walk into your interview confident, prepared, and ready to impress"
          features={features}
        /> */}

               {/* How It Works Section */}
               <section id="how-it-works" className="pt-4 sm:pt-8 lg:pt-12 pb-8 sm:pb-12 lg:pb-16 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
          <div className="max-w-[1200px] mx-auto w-full px-4">
            <div className="text-center mb-8 sm:mb-10 lg:mb-20">
              <motion.h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-brand-dark-blue"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Trans>How It Works</Trans>
              </motion.h2>
              <motion.p
                className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Trans>Practice answering interview questions, get AI feedback, and walk in confident</Trans>
              </motion.p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3  items-center relative">
              {/* Connecting dotted lines - desktop only */}
              {/* <div className="hidden lg:block absolute top-[120px] left-0 right-0 h-0.5 pointer-events-none z-0">
                <svg className="w-full h-full" preserveAspectRatio="none">
                  <line x1="25%" y1="0" x2="40%" y2="0" stroke="#F97316" strokeWidth="2" strokeDasharray="8 8" />
                  <line x1="60%" y1="0" x2="75%" y2="0" stroke="#F97316" strokeWidth="2" strokeDasharray="8 8" />
                </svg>
              </div> */}

              {/* Step 1 - Record (PRE-CALL style) */}
              <motion.div
                className="flex flex-col items-center text-center relative z-10"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="bg-gradient-to-br from-brand-gold/10 to-white rounded-3xl p-6 sm:p-8 w-full border h-[500px]">
                  {/* Badge */}
                  <div className="mb-6">
                    <span className="inline-block bg-gray-400 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide">
                      <Trans>PRACTICE</Trans>
                    </span>
                  </div>

                  {/* Demo Illustration - Original */}
                  <div className="relative mb-8 w-full h-[220px]">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden transform transition-transform duration-300 hover:scale-105 h-full">
                      <div className="relative h-full bg-cover bg-center" style={{ backgroundImage: 'url(/gifs/sales_vid.gif)' }}>
                        {/* Overlay for better contrast */}
                        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30"></div>
                        {/* Recording indicator */}
                        {/* <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-full text-xs font-bold z-10 shadow-lg">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          REC
                        </div> */}
                        {/* Timer */}
                        {/* <div className="absolute top-3 right-3 bg-brand-dark-blue/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full font-mono text-xs font-bold z-10 shadow-lg">
                          01:45
                        </div> */}
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    <Trans>Answer Questions</Trans>
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    <Trans>Pick a role, get real interview questions, and answer out loud — just like the real thing.</Trans>
                  </p>
                </div>
              </motion.div>

              {/* Step 2 - AI Analysis (DURING CALL style - elevated) */}
              <motion.div
                className="flex flex-col items-center text-center relative z-20 lg:-my-4"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="bg-white rounded-3xl p-6 sm:p-8 w-full shadow-2xl border h-[532px]">
                  {/* Badge - Highlighted like "DURING CALL" */}
                  <div className="mb-6">
                    <span className="inline-block bg-gradient-to-r from-brand-gold to-brand-gold/80 text-white px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wide shadow-lg">
                      <Trans>ANALYZE</Trans>
                    </span>
                  </div>
                  
                  {/* Demo Illustration - Speech Analysis */}
                  <div className="relative mb-8 w-full h-[220px]">
                    <div className="bg-white rounded-2xl  p-4 transform transition-transform duration-300 hover:scale-105 h-full flex flex-col justify-center relative overflow-hidden">
                      {/* Animated scanning line */}
                      <motion.div
                        className="absolute left-0 right-0 h-0.5 bg-brand-gold shadow-lg shadow-brand-gold/50 z-10"
                        animate={{
                          top: ['10%', '90%', '10%']
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                      
                      <div className="space-y-3 relative z-0">
                        {/* Audio Waveform */}
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-gold to-brand-gold/80 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </div>
                          <div className="flex items-center gap-0.5 flex-1 max-w-[200px]">
                            {[4, 8, 6, 10, 5, 12, 8, 6, 10, 7, 11, 6, 9, 5, 8, 10, 7, 9, 6, 8, 5, 7, 9, 11, 6].map((height, i) => (
                              <motion.div
                                key={i}
                                className="w-1 bg-gradient-to-t from-brand-gold to-brand-gold/80 rounded-full"
                                style={{ height: `${height * 2}px` }}
                                animate={{
                                  height: [`${height * 2}px`, `${height * 2.5}px`, `${height * 2}px`]
                                }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                  delay: i * 0.05
                                }}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Speech Bubble with Avatar */}
                        <div className="flex items-start gap-3 px-2">
                          {/* User Avatar */}
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-gray-200 bg-white">
                            <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: 'url(/images/academy/salesguy.png)' }} />
                          </div>
                          
                          {/* Speech Bubble */}
                          <div className="flex-1 bg-gray-50 rounded-2xl rounded-tl-sm p-3 border border-gray-200">
                            {/* <div className="text-[10px] text-gray-500 font-bold mb-1 uppercase tracking-wide">
                              Speaker
                            </div> */}
                            <div className="text-xs text-gray-700 leading-relaxed">
                              <Trans>"In my previous role, I led a cross-functional team that increased retention by 30%..."</Trans>
                            </div>
                          </div>
                        </div>

                        {/* Analysis indicators */}
                        <div className="flex items-center justify-center gap-2 pt-1">
                          <motion.div
                            className="flex items-center gap-1 text-[10px] text-gray-600"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <div className="w-1.5 h-1.5 bg-brand-gold rounded-full animate-pulse" />
                            <span><Trans>Analyzing tone</Trans></span>
                          </motion.div>
                          <motion.div
                            className="flex items-center gap-1 text-[10px] text-gray-600"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          >
                            <div className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" />
                            <span><Trans>Detecting pace</Trans></span>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    <Trans>AI Analysis</Trans>
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    <Trans>Pavone transcribes your answer and analyzes clarity, confidence, pace, and structure — like having a coach review every word.</Trans>
                  </p>
                </div>
              </motion.div>

              {/* Step 3 - Get Feedback (POST-CALL style) */}
              <motion.div
                className="flex flex-col items-center text-center relative z-10"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="bg-gradient-to-br from-brand-gold/10 to-white rounded-3xl p-6 sm:p-8 w-full border h-[500px]">
                  {/* Badge */}
                  <div className="mb-6">
                    <span className="inline-block bg-gray-400 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide">
                      <Trans>IMPROVE</Trans>
                    </span>
                  </div>
                  
                  {/* Demo Illustration - Interview Feedback */}
                  <div className="relative mb-8 w-full h-[220px]">
                    <div className="bg-white rounded-2xl overflow-hidden transform transition-transform duration-300 hover:scale-105 h-full relative">
                      <div className="h-full p-3 flex flex-col justify-center bg-gradient-to-br from-white to-gray-50/50">
                        <div className="space-y-2">
                          {/* Interview Readiness + Communication Level */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-brand-dark-blue/5 rounded-lg p-2 border border-brand-dark-blue/10 shadow-sm">
                              <div className="text-[9px] text-brand-dark-blue mb-1 text-left font-bold"><Trans>Interview Readiness</Trans></div>
                              <div className="flex items-baseline gap-1">
                                <span className="text-lg font-bold text-green-500">78%</span>
                                <span className="text-[9px] text-green-600 font-medium"><Trans>Good</Trans></span>
                              </div>
                            </div>
                            <div className="bg-brand-dark-blue/5 rounded-lg p-2 border border-brand-dark-blue/10 shadow-sm">
                              <div className="text-[9px] text-brand-dark-blue mb-1 text-left font-bold"><Trans>Communication Level</Trans></div>
                              <div className="flex items-baseline gap-1">
                                <span className="text-sm font-bold text-brand-dark-blue">7.2 / 10</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                                <motion.div
                                  className="bg-brand-gold h-1 rounded-full"
                                  initial={{ width: '0%' }}
                                  whileInView={{ width: '72%' }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 0.8, delay: 0.6 }}
                                />
                              </div>
                              <div className="text-[8px] text-gray-500 mt-0.5"><Trans>Mid-Level</Trans></div>
                            </div>
                          </div>

                          {/* What to Focus on Next */}
                          <div className="space-y-1.5 bg-white rounded-lg p-2 border border-gray-100 shadow-sm">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold text-gray-700"><Trans>What to Focus on Next</Trans></span>
                            </div>

                            <div className="space-y-1.5 ml-0.5">
                              <div className="flex items-start gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1 flex-shrink-0"></div>
                                <span className="text-[9px] text-gray-600 text-left leading-tight"><Trans>Structure your answer with a clear beginning, middle, end</Trans></span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-1 flex-shrink-0"></div>
                                <span className="text-[9px] text-gray-600 text-left leading-tight"><Trans>Reduce filler words in opening sentences</Trans></span>
                              </div>
                              <div className="flex items-start gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1 flex-shrink-0"></div>
                                <span className="text-[9px] text-gray-600 text-left leading-tight"><Trans>Add specific metrics to strengthen your examples</Trans></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Blur overlay at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/60 to-transparent pointer-events-none rounded-b-2xl"></div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    <Trans>Get Feedback</Trans>
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed">
                    <Trans>See your Interview Readiness score, communication level, and exactly what to improve for your next attempt.</Trans>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

          {/* Features Section */}
          <section id="features" className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-[1080px] mx-auto w-full px-4">
              <div className="text-center mb-8 sm:mb-10 lg:mb-12">
                <motion.h2
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-brand-dark-blue"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Trans>Features</Trans>
                </motion.h2>
                <motion.p
                  className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Trans>Everything you need to improve your speaking skills with AI-powered insights</Trans>
                </motion.p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {/* === ROW 1: Hook — strongest differentiators === */}

                {/* Feature 1: Per Question Feedback */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-white flex items-center justify-center overflow-hidden p-4">
                    <div className="w-full max-w-[280px] space-y-2">
                      {/* Question header */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-brand-dark-blue text-white flex items-center justify-center font-bold text-[10px]">
                          1
                        </div>
                        <span className="text-[11px] font-semibold text-brand-dark-blue leading-tight">Tell me about yourself</span>
                      </div>
                      {/* Strength */}
                      <div className="flex items-center gap-2 p-2 rounded-md border border-brand-success/30 bg-brand-success/5">
                        <svg className="w-3.5 h-3.5 text-brand-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[10px] text-gray-700">Clear STAR method structure</span>
                      </div>
                      {/* Weakness */}
                      <div className="flex items-center gap-2 p-2 rounded-md border border-red-300 bg-red-50">
                        <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" strokeWidth={2} />
                          <line x1="12" y1="8" x2="12" y2="12" strokeWidth={2} />
                          <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth={2} />
                        </svg>
                        <span className="text-[10px] text-gray-700">Add specific metrics</span>
                      </div>
                      {/* Suggestion */}
                      <div className="flex items-center gap-2 p-2 rounded-md border border-brand-gold/30 bg-brand-gold/5">
                        <svg className="w-3.5 h-3.5 text-brand-gold flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-[10px] text-gray-700">Quantify your achievements</span>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>DEEP ANALYSIS</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Per Question Feedback</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>Get strengths, weaknesses, and actionable suggestions analyzed individually for each interview question.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 2: Hiring Manager Summary */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-brand-dark-blue/5 flex items-center justify-center overflow-hidden p-4">
                    {/* Hiring manager feedback card mockup */}
                    <div className="bg-white rounded-xl shadow-lg p-4 w-full max-w-[280px] border border-brand-dark-blue/10">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-brand-dark-blue/10 flex items-center justify-center">
                          <Briefcase className="w-4 h-4 text-brand-dark-blue" />
                        </div>
                        <span className="text-xs font-bold text-brand-dark-blue">Hiring Manager</span>
                      </div>
                      <p className="text-xs text-gray-600 italic leading-relaxed">
                        "Strong communicator with clear articulation. Answers are well-structured and confident..."
                      </p>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>AI INSIGHT</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Manager Summary</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>See how a hiring manager would perceive your interview. Get a concise assessment of your clarity, confidence, and overall impression.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 3: Interview Readiness */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-brand-gold/5 flex items-center justify-center overflow-hidden p-4">
                    <div className="flex items-center gap-6">
                      {/* Circular gauge */}
                      <div className="relative">
                        <svg className="w-20 h-20" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                          <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="8"
                            strokeDasharray="200" strokeDashoffset="44" strokeLinecap="round"
                            transform="rotate(-90 50 50)" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-lg font-bold text-brand-dark-blue">78%</span>
                          <span className="text-[10px] text-gray-500">Ready</span>
                        </div>
                      </div>
                      {/* Linear bar */}
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-600">Communication</div>
                        <div className="text-lg font-bold text-brand-dark-blue">7/10</div>
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="w-[70%] h-full bg-gradient-to-r from-brand-gold to-brand-dark-blue rounded-full" />
                        </div>
                        <div className="text-[10px] text-brand-gold font-medium">Mid-Level</div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>READINESS METER</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Interview Readiness</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>Know exactly when you're ready. Get a percentage score showing your preparation level and communication seniority rating.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* === ROW 2: Core value — tangible features === */}

                {/* Feature 4: Filler Words Detection */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-white flex items-center justify-center overflow-hidden p-4">
                    <div className="space-y-2 w-full">
                      {/* Text with highlighted filler words */}
                      <div className="text-center space-y-1">
                        <div className="text-sm text-gray-600">
                          <Trans>I <span className="bg-yellow-200 px-1 rounded">uh</span> think we should <span className="bg-yellow-200 px-1 rounded">um</span> proceed <span className="bg-yellow-200 px-1 rounded">like</span></Trans>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <svg className="w-4 h-4 text-brand-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        </div>
                        <div className="text-sm text-brand-green font-medium">
                          <Trans>I think we should proceed</Trans>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>CLARITY BOOST</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Eliminate Filler Words</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>Instantly detect "um," "uh," "like," and other distractions.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 5: What to Focus on Next */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-green-50 flex items-center justify-center overflow-hidden p-4">
                    <div className="bg-white rounded-xl shadow-lg p-4 w-full max-w-[280px] border border-green-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-bold text-gray-700">Focus Areas</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500" />
                          <span className="text-[11px] text-gray-600">Reduce filler words</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-brand-gold" />
                          <span className="text-[11px] text-gray-600">Slow down pace slightly</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500" />
                          <span className="text-[11px] text-gray-600">Add more energy</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>SMART PRIORITIES</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>What to Focus on Next</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>No guesswork. Get a prioritized list of exactly what to improve for your next practice session.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 6: Personalized AI Tips */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-blue-50 flex items-center justify-center overflow-hidden p-4">
                    <div className="space-y-2 w-full">
                      {/* AI Tips */}
                      <div className="bg-white rounded-lg p-3 shadow-lg border-l-4 border-brand-green">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-xs text-gray-700"><Trans>Slow down 15% for more impact</Trans></p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-lg border-l-4 border-brand-gold">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-brand-gold mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <p className="text-xs text-gray-700"><Trans>Great energy! Maintain this volume</Trans></p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-lg border-l-4 border-brand-dark-blue">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-brand-dark-blue mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <p className="text-xs text-gray-700"><Trans>Add pauses for emphasis</Trans></p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>PERSONALIZED</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Custom AI Feedback</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>No generic advice. Get personalized tips based on your unique speaking style, intent, and specific areas for improvement.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* === ROW 3: Supporting — utility features === */}

                {/* Feature 7: AI Script Improver */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-white flex items-center justify-center overflow-hidden p-6">
                    <div className="space-y-2 w-full">
                      {/* Script lines */}
                      <div className="bg-white rounded-lg p-3 shadow-lg">
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-full bg-brand-gold rounded"></div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-600 line-through"><Trans>Let me tell you about our product</Trans></p>
                            <p className="text-sm text-brand-dark-blue font-medium mt-1"><Trans>✨ Discover how our solution transforms your workflow</Trans></p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-3 shadow-lg">
                        <div className="flex items-start gap-2">
                          <div className="w-1 h-full bg-brand-gold rounded"></div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-600 line-through"><Trans>It's really good</Trans></p>
                            <p className="text-sm text-brand-dark-blue font-medium mt-1"><Trans>✨ Drive 3x faster results</Trans></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>AI REWRITE</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>AI Script Improver</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>Get AI suggestions for maximum impact, then save your improved script for next time.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 8: Voice Bell Curve Comparison */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-brand-gold/10 flex items-center justify-center overflow-hidden p-6">
                    <div className="w-full">
                      {/* Bell curve SVG */}
                      <svg viewBox="0 0 200 100" className="w-full h-auto">
                        {/* Bell curve path */}
                        <path
                          d="M 10 90 Q 50 10, 100 10 T 190 90"
                          fill="none"
                          stroke="#8B793D"
                          strokeWidth="2"
                        />
                        {/* Marker for "You" */}
                        <circle cx="120" cy="40" r="4" fill="#D4AF37" />
                        <text x="120" y="30" textAnchor="middle" fontSize="10" fill="#D4AF37" fontWeight="bold">You</text>
                        {/* Labels */}
                        <text x="10" y="95" fontSize="8" fill="#666">Low</text>
                        <text x="95" y="95" textAnchor="middle" fontSize="8" fill="#666">Average</text>
                        <text x="185" y="95" textAnchor="end" fontSize="8" fill="#666">High</text>
                      </svg>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>DATA-DRIVEN</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Compare Voice</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>See how your pitch, speaking rate, and volume compare to population averages.</Trans>
                    </p>
                  </div>
                </motion.div>

                {/* Feature 9: 24/7 Practice */}
                <motion.div
                  className="bg-gray-50 rounded-3xl border-2 border-gray-200 hover:border-brand-gold/50 transition-all duration-300 flex flex-col overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {/* Demo Illustration - Top */}
                  <div className="relative h-[200px] bg-gradient-to-br from-white to-brand-gold/10 flex items-center justify-center overflow-hidden">
                    <div className="relative">
                      {/* Clock Icon */}
                      <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-xl">
                        <svg className="w-12 h-12 text-brand-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      {/* 24/7 Badge */}
                      <div className="absolute -top-2 -right-2 bg-brand-green text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        24/7
                      </div>
                    </div>
                  </div>

                  {/* Text Content - Bottom */}
                  <div className="flex-1 bg-white p-6 sm:p-8 pb-8 flex flex-col">
                    <div className="inline-block bg-brand-gold/20 text-brand-gold px-3 py-1 rounded-full text-xs font-bold mb-3 w-fit">
                      <Trans>ALWAYS AVAILABLE</Trans>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-3"><Trans>Your 24/7 AI Coach</Trans></h3>
                    <p className="text-gray-600 leading-relaxed">
                      <Trans>No appointments needed. Practice anytime, anywhere.</Trans>
                    </p>
                  </div>
                </motion.div>

              </div>
            </div>
          </section>
          {/* <motion.div
              className="flex justify-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <TryForFreeButton 
                size="lg" 
                text={isSignedIn ? "LET'S GO" : "TRY FOR FREE"}
                href={isSignedIn ? postAuthPath : "/sign-up"}
              />
            </motion.div> */}



        <Reviews />

        {/* <PrivacySection /> */}

        <PricingSection />

        <FAQSection />

        {/* Final CTA Section */}
        {/* <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-dark-blue to-brand-dark-blue/90 text-white">
          <div className="max-w-[1080px] mx-auto w-full px-4">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                Your next interview is<br />too important to wing it
              </h2>
              <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Join 12,000+ job seekers who practiced with Pavone and landed their dream jobs. 
                Start free — no credit card required.
              </p>
              
              <motion.a
                href="/sign-up"
                className="inline-flex items-center px-8 sm:px-12 py-4 sm:py-5 h-12 sm:h-14 text-base sm:text-lg font-semibold rounded-2xl bg-gradient-to-r from-[#8B793D] via-[#8B793D]/80 to-[#8B793D] text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#8B793D]/60 via-[#8B793D]/40 to-[#8B793D]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative z-10">START FREE PRACTICE</span>
                <div className="absolute inset-0 rounded-2xl bg-white/20 scale-0 group-hover:scale-100 transition-transform duration-300"></div>
              </motion.a>

           
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 pt-6 sm:pt-8 text-gray-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">Start practicing in 2 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                  <span className="text-sm">12,000+ job offers secured</span>
                </div>
              </div>
            </div>
          </div>
        </section> */}
        {/* <section className="py-6 sm:py-14 lg:py-18 px-4 sm:px-6 lg:px-8 pb-14 sm:pb-18 mb-8">
          <div className="max-w-[1080px] mx-auto w-full px-4">
            <div className="text-center space-y-4 sm:space-y-6">
              <h2 className="pt-8 sm:pt-0 text-4xl sm:text-5xl md:text-5xl font-bold leading-tight text-brand-dark-blue">
                <Trans>Your next interview is <br className="hidden sm:block" /><span className="text-brand-gold">too important</span> to wing it</Trans>
              </h2>
            
              
         

           
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 md:gap-12 py-4 mb-8 sm:mb-10"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base font-medium"><Trans>Sound confident and senior in interviews</Trans></span>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base font-medium"><Trans>Start practicing now</Trans></span>
                </div>
                <div className="flex items-center gap-3 sm:gap-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm sm:text-base font-medium"><Trans>Instant AI feedback</Trans></span>
                </div>
              </motion.div>
              <TryForFreeButton
                size="lg"
                text={isSignedIn ? t`LET'S GO` : t`TRY FOR FREE`}
                href={isSignedIn ? postAuthPath : "/sign-up"}
              />
            </div>
          </div>
        </section> */}
      </main>
 
      <Footer />
    </div>
  );
};

export default Interview;

if (typeof document !== 'undefined') {
  document.documentElement.style.scrollBehavior = 'smooth';
}
